/**
 * Durable persistence for founder fundraising round history.
 *
 * Ownership lives in `team_members`, so the caller resolves `startupId` via the
 * membership seam (`useMyStartup` / `resolveMyStartup`) and passes it in — the
 * legacy `startup_profiles.user_id` is NULL for every create-path founder and
 * must never gate these writes. The `fundraising_rounds` RLS was migrated to
 * the same `is_active_startup_team_member` model (see
 * supabase/migrations/*_fundraising_rounds_team_scope.sql).
 *
 * The onboarding UI only ADDS and REMOVES rounds (no in-place edit), so `save`
 * reconciles by insert + delete rather than diffing field values. New rounds
 * carry a client-only `round-<ts>` id and are inserted WITHOUT an id (the DB
 * default mints the uuid); loaded rounds carry their real uuid. `verified` /
 * `verified_by` are investor-owned and never written on a founder insert.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/lib/supabase/database.types';
import type { FundraisingRound } from '@/lib/state/profile-store';

interface FundraisingRoundRow {
  id: string;
  startup_id: string;
  stage: string;
  amount: number | string;
  date: string;
  lead_investor: string | null;
  verified: boolean | null;
  verified_by: string | null;
}

/** UI month (`YYYY-MM`) → DB `date` (`YYYY-MM-DD`). A full date passes through. */
export function roundDateToDb(uiDate: string): string {
  return /^\d{4}-\d{2}$/.test(uiDate) ? `${uiDate}-01` : uiDate;
}

/** DB `date` (`YYYY-MM-DD`) → the `YYYY-MM` month the UI enters and displays. */
export function dbDateToRound(dbDate: string): string {
  return dbDate.slice(0, 7);
}

export function rowToRound(row: FundraisingRoundRow): FundraisingRound {
  return {
    id: row.id,
    stage: row.stage as FundraisingRound['stage'],
    amount: Number(row.amount),
    date: dbDateToRound(row.date),
    leadInvestor: row.lead_investor ?? undefined,
    verified: row.verified ?? false,
    verifiedBy: row.verified_by ?? undefined,
  };
}

export async function loadFundraisingRounds(
  client: SupabaseClient<Database>,
  startupId: string,
): Promise<FundraisingRound[]> {
  const db = client as any;
  const { data, error } = await db
    .from('fundraising_rounds')
    .select('*')
    .eq('startup_id', startupId)
    .order('date', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(rowToRound);
}

/**
 * Reconcile the founder's desired round set against what's persisted: insert
 * rounds not yet in the DB, delete rounds the founder removed. Kept rounds
 * (already carrying a DB uuid) are untouched, preserving any investor
 * `verified` state. Throws on any write error — callers must not treat a
 * failure as saved.
 */
export async function saveFundraisingRounds(
  client: SupabaseClient<Database>,
  startupId: string,
  rounds: FundraisingRound[],
): Promise<void> {
  const db = client as any;

  const { data: existing, error: selError } = await db
    .from('fundraising_rounds')
    .select('id')
    .eq('startup_id', startupId);
  if (selError) throw selError;

  const existingIds = new Set<string>((existing ?? []).map((r: { id: string }) => r.id));
  const keptIds = rounds.map((r) => r.id).filter((id) => existingIds.has(id));

  const toDelete = [...existingIds].filter((id) => !keptIds.includes(id));
  if (toDelete.length > 0) {
    const { error } = await db.from('fundraising_rounds').delete().in('id', toDelete);
    if (error) throw error;
  }

  const toInsert = rounds.filter((r) => !existingIds.has(r.id));
  if (toInsert.length > 0) {
    const rows = toInsert.map((r) => ({
      startup_id: startupId,
      stage: r.stage,
      amount: r.amount,
      date: roundDateToDb(r.date),
      lead_investor: r.leadInvestor ?? null,
    }));
    const { error } = await db.from('fundraising_rounds').insert(rows);
    if (error) throw error;
  }
}
